import cx from 'classnames'

import Styles from './PlanDetail.module.scss'

import { PaymentPlan, PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Features } from './components/Features/Features'
import { Button } from 'components/Button'
import { ButtonLink } from 'components/Button/ButtonLink'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { BillingFrequency } from 'components/Paywall/UpgradePlanModal/UpgradePlanModal'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useMutation } from 'react-query'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { Workspace } from 'types'
import { workspacesSet } from 'redux/actions'
import { useDispatch } from 'react-redux'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

interface Props {
  billingFrequency: BillingFrequency
  savedPlan: PaymentPlan
  selectPlan: (plan: PaymentPlan) => void
  plan: PaymentPlan
  isDisabled: boolean
  isCanceled?: boolean
}

export const upgradeOrDowngrade = (savedPlan: PaymentPlan, plan: PaymentPlan, isCanceled?: boolean) => {
  const availablePlans = [PaymentPlanKey.Free, PaymentPlanKey.BlazeCreator, PaymentPlanKey.BlazeTeam]
  const savedPlanIndex = availablePlans.indexOf(savedPlan.key)
  const planIndex = availablePlans.indexOf(plan.key)

  if (savedPlanIndex > planIndex) return 'Downgrade'

  return isCanceled && savedPlan.key === plan.key ? 'Reactivate' : 'Upgrade'
}

export const PlanDetail = ({ billingFrequency, savedPlan, selectPlan, plan, isDisabled, isCanceled }: Props) => {
  const workspace = useCurrentWorkspace()
  const { data: currentPlan } = useCurrentPlan()
  const resumeSubscription = useMutation(WorkspaceClient.unpauseSubscription)
  const workspaces = useWorkspaces()
  const dispatch = useDispatch()
  const isBlackFridayCtaEnabled = useFlipperFlag('black_friday_cta')

  const discountedPrice = () => {
    if (plan.key === PaymentPlanKey.Free) return 'Free'

    let discountedPrice

    if (billingFrequency === 'month') {
      const { unit_amount, amount_off } = plan.price.month
      discountedPrice = unit_amount - (amount_off || 0)
    } else {
      const { unit_amount, amount_off } = plan.price.year
      discountedPrice = (unit_amount - (amount_off || 0)) / 12
    }

    if (discountedPrice % 1) return `$${discountedPrice.toFixed(2)}`

    return `$${discountedPrice}`
  }

  const buttonText = () => {
    if (isCurrentPlan) {
      if (isCanceled) return 'Reactivate'
      if (workspace?.collection_paused) return 'Resume'

      return 'Your Current Plan'
    }

    if ([PaymentPlanKey.Enterprise, PaymentPlanKey.BlazeBusiness].includes(plan.key)) return 'Contact Sales'

    if (workspace?.paying) {
      return upgradeOrDowngrade(savedPlan, plan, isCanceled)
    } else {
      return 'Subscribe'
    }
  }

  const isCurrentPlan = plan.key === savedPlan.key && billingFrequency === currentPlan?.billing_frequency
  const mailTo = 'mailto:support@blaze.ai'

  return (
    <div className={cx(Styles.planDetail, { [Styles.borderRight]: plan.key !== PaymentPlanKey.Enterprise })}>
      <div className={cx(Styles.planRibbon, Styles[plan.key])}></div>
      <div className={Styles.contents}>
        <div className={Styles.nameAndPrice}>
          <p className={Styles.name}>{plan.name}</p>
          <div className={Styles.priceDetails}>
            <div className={Styles.costContainer}>
              <p
                className={cx(Styles.discountedPrice, {
                  [Styles.hide]: [PaymentPlanKey.Enterprise, PaymentPlanKey.BlazeBusiness].includes(plan.key),
                })}
              >
                {discountedPrice()} USD/mo
              </p>
              {billingFrequency === 'year' && plan.key !== PaymentPlanKey.BlazeBusiness && (
                <div className={Styles.billedYearlyContainer}>
                  <p className={Styles.billedYearly}>Billed yearly</p>
                  <p
                    className={cx(Styles.threeMonthsFree, Styles[plan.key], {
                      [Styles.blackFridayCta]: isBlackFridayCtaEnabled,
                    })}
                  >
                    {isBlackFridayCtaEnabled ? 'Black Friday Sale - Save 42%' : 'Save 25%'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        <Features plan={plan} billingFrequency={billingFrequency} />
        <Tooltip
          placement="bottom"
          label={<>{`Not eligible due to more than ${plan.member_limit} workspace users`}</>}
          hide={!isDisabled}
        >
          {[PaymentPlanKey.Enterprise, PaymentPlanKey.BlazeBusiness].includes(plan.key) ? (
            <ButtonLink
              className={cx(Styles.actionButton, { [Styles.current]: isCurrentPlan })}
              data-testid={`${plan.name.toLowerCase()}-button`}
              to={mailTo}
              size="lg"
              isDisabled={isDisabled || isCurrentPlan}
              variant="secondary"
            >
              {buttonText()}
            </ButtonLink>
          ) : (
            <Button
              className={cx(Styles.actionButton, {
                [Styles.current]: !isCanceled && isCurrentPlan && !workspace?.collection_paused,
              })}
              data-testid={`${plan.name.toLowerCase()}-button`}
              onPress={() => {
                if (!isCurrentPlan || isCanceled) {
                  selectPlan(plan)
                }

                if (workspace?.collection_paused) {
                  resumeSubscription.mutate(
                    { workspaceId: workspace.id },
                    {
                      onSuccess: () => {
                        const updatedWorkspaces = workspaces.map((ws) => {
                          if (ws.id === workspace?.id) {
                            return { ...workspace, collection_paused: false } as Workspace
                          }

                          return ws
                        })
                        dispatch(workspacesSet(updatedWorkspaces))
                      },
                      onError: () => {
                        showToastMessage('Unable to resume your subscription', 'error')
                      },
                    },
                  )
                }
              }}
              size="lg"
              isDisabled={
                !isCanceled &&
                (isDisabled || (isCurrentPlan && !workspace?.collection_paused) || resumeSubscription.isLoading)
              }
              variant="secondary"
            >
              {buttonText()}
            </Button>
          )}
        </Tooltip>
      </div>
    </div>
  )
}
